import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'

import ClockIcon from '@common/assets/svg/ClockIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import flexbox from '@common/styles/utils/flexbox'
import { DcaOrder, DcaToken } from '@web/extension-services/background/controllers/dca/types'

import { formatDcaAmount, formatDcaDateTimeShort, formatDcaInterval } from '../format'
import { getPauseReasonText } from './historyText'
import { getProgressPercent, getToken, getUpcomingExecutionAt } from './orderView'
import { DcaPair, DcaProgressBar, DcaStatusPill, tokenLabel } from './parts'

type Props = {
  order: DcaOrder
  tokens: Record<string, DcaToken>
  onPress: (orderId: string) => void
  /** A cancel of this order is being signed or mined. */
  isCancelling?: boolean
}

/** The note under the progress bar: what happens next, in the status colour. */
const useOrderNote = (order: DcaOrder, isCancelling: boolean) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  if (isCancelling) return { text: t('Stopping future buys...'), color: theme.warningText }
  switch (order.status) {
    case 'CANCELLED':
      return { text: t('No more buys scheduled'), color: theme.errorText }
    case 'COMPLETED':
      return { text: t('All buys completed'), color: theme.successText }
    case 'EXPIRED':
      return { text: t('Expired before all buys ran'), color: theme.secondaryText }
    case 'PAUSED':
      return { text: getPauseReasonText(order.pauseReason, t), color: theme.warningText }
    default:
      if (!order.supported)
        return { text: t('This pair is no longer supported'), color: theme.warningText }
      if (getUpcomingExecutionAt(order.nextExecutionAt))
        return {
          text: t('Next: {{date}}', { date: formatDcaDateTimeShort(order.nextExecutionAt) }),
          color: theme.secondaryText,
          withClock: true
        }
      return { text: t('Waiting for the next buy'), color: theme.secondaryText }
  }
}

const DcaOrderCard = ({ order, tokens, onPress, isCancelling = false }: Props) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const tokenIn = getToken(tokens, order.tokenIn)
  const tokenOut = getToken(tokens, order.tokenOut)
  const percent = getProgressPercent(order.executedCount, order.totalExecutions)
  const note = useOrderNote(order, isCancelling)

  return (
    <Pressable
      onPress={() => onPress(order.orderId)}
      testID={`dca-order-card-${order.orderId}`}
      style={({ hovered }: any) => ({
        backgroundColor: (hovered ? theme.secondaryBackground : theme.homeCardBackground) as string,
        borderWidth: 1,
        borderColor: theme.homeCardBorder as string,
        borderRadius: RADII.md,
        padding: 16,
        gap: 12
      })}
    >
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          { gap: 12 }
        ]}
      >
        <DcaStatusPill status={isCancelling ? 'CANCELLING' : order.status} />
        <DcaPair
          tokenIn={tokenIn}
          tokenOut={tokenOut}
          tokenInAddress={order.tokenIn}
          tokenOutAddress={order.tokenOut}
        />
      </View>

      <Text fontSize={14} weight="semiBold">
        {t('{{amount}} {{symbol}} every {{interval}}', {
          amount: tokenIn ? formatDcaAmount(order.amountPerExecutionRaw, tokenIn.decimals) : '-',
          symbol: tokenLabel(tokenIn, order.tokenIn),
          interval: formatDcaInterval(order.interval, t)
        })}
      </Text>

      <View style={{ gap: 8 }}>
        <DcaProgressBar percent={percent} />
        <View style={[flexbox.directionRow, flexbox.justifySpaceBetween, { gap: 12 }]}>
          <Text fontSize={13} appearance="secondaryText">
            {t('{{done}} of {{count}} buys completed', {
              done: order.executedCount,
              count: order.totalExecutions
            })}
          </Text>
          <Text fontSize={13} weight="medium">{`${percent}%`}</Text>
        </View>
      </View>

      <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 6 }]}>
        {!!note.withClock && <ClockIcon width={14} height={14} color={note.color as string} />}
        <Text fontSize={13} weight="medium" color={note.color as string} style={{ flexShrink: 1 }}>
          {note.text}
        </Text>
      </View>
    </Pressable>
  )
}

export default React.memo(DcaOrderCard)
